import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCmsPanel } from '@/contexts/CmsPanelContext';

/* ── Internal context to pass onOpenChange to PanelTrigger ──────────── */
const FormPanelCtx = React.createContext<{ onOpen: () => void }>({ onOpen: () => {} });

/* ── CmsFormPanel — drop-in replacement for Sheet ──────────────────── */
export function CmsFormPanel({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const { openPanel, closePanel, panelContainer } = useCmsPanel();

  useEffect(() => {
    if (open) openPanel();
    else closePanel();
  }, [open, openPanel, closePanel]);

  // Separate PanelContent from other children (triggers, etc.)
  const triggers: React.ReactNode[] = [];
  let panelContent: React.ReactElement | null = null;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === PanelContent) {
      panelContent = child;
    } else {
      triggers.push(child);
    }
  });

  return (
    <FormPanelCtx.Provider value={{ onOpen: () => onOpenChange(true) }}>
      {triggers}
      {open && panelContainer && panelContent && createPortal(
        <div className="relative p-6 h-full flex flex-col">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          {(panelContent as React.ReactElement<{ children: React.ReactNode }>).props.children}
        </div>,
        panelContainer
      )}
    </FormPanelCtx.Provider>
  );
}

/* ── PanelTrigger — drop-in replacement for SheetTrigger ───────────── */
export function PanelTrigger({ asChild, children }: { asChild?: boolean; children: React.ReactNode }) {
  const { onOpen } = React.useContext(FormPanelCtx);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onClick: (e: React.MouseEvent) => {
        (children as React.ReactElement<any>).props.onClick?.(e);
        onOpen();
      },
    });
  }

  return <button type="button" onClick={onOpen}>{children}</button>;
}

/* ── PanelContent — marker component, children extracted by CmsFormPanel */
export function PanelContent({ children, className, side }: {
  children: React.ReactNode;
  className?: string;
  side?: string;
}) {
  return <>{children}</>;
}

/* ── PanelHeader — drop-in replacement for SheetHeader ─────────────── */
export function PanelHeader({ children, className }: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn('flex flex-col space-y-2 text-left', className)}>{children}</div>;
}

/* ── PanelTitle — drop-in replacement for SheetTitle ───────────────── */
export function PanelTitle({ children, className }: {
  children: React.ReactNode;
  className?: string;
}) {
  return <h2 className={cn('text-lg font-semibold text-foreground', className)}>{children}</h2>;
}
